import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { keralitProfileSystems, normalizeKeralitProfileSystem } from '../data/mockData';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import ProfileSystemPicker from './ProfileSystemPicker';
import { useTranslation } from '../context/TranslationContext';
import {
  KERALIT_MIN_DIMENSION_MM,
  KERALIT_MAX_DIMENSION_MM,
} from '../lib/pdfPricing';

const KERALIT_MIN_DIMENSION = KERALIT_MIN_DIMENSION_MM;
const KERALIT_MAX_DIMENSION = KERALIT_MAX_DIMENSION_MM;

const isKeralitProfileSelected = (configProfileId, optionProfileId) =>
  normalizeKeralitProfileSystem(configProfileId) === normalizeKeralitProfileSystem(optionProfileId);

const KeralitOptionsPanel = ({
  config,
  onConfigChange,
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();

  const [widthInput, setWidthInput] = useState(String(config.width ?? 1500));
  const [heightInput, setHeightInput] = useState(String(config.height ?? 1500));

  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);

  const commitDimension = (field, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.min(
      KERALIT_MAX_DIMENSION,
      Math.max(KERALIT_MIN_DIMENSION, parsed)
    );
    onConfigChange(field, clamped);
  };

  const dimensionInputProps = (field, inputValue, setInputValue) => ({
    type: 'number',
    min: KERALIT_MIN_DIMENSION,
    max: KERALIT_MAX_DIMENSION,
    value: inputValue,
    onChange: (e) => setInputValue(e.target.value),
    onBlur: () => commitDimension(field, inputValue),
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        commitDimension(field, inputValue);
      }
    },
    className: 'mt-1',
  });

  return (
    <div className="space-y-4">
      <ProfileSystemPicker
        profileSystems={keralitProfileSystems}
        config={config}
        onConfigChange={onConfigChange}
        title={t('options.profileSystem')}
        description={t('options.selectKeralitProfile')}
        normalizeProfileId={normalizeKeralitProfileSystem}
        isProfileSelected={isKeralitProfileSelected}
        translateName={(id, name) =>
          ({
            'potdeksel-190': t('options.keralitPotdeksel190'),
            'potdeksel-177': t('options.keralitPotdeksel190'),
            'sponningdeel-143': t('options.keralitSponningdeel143'),
          }[id] || name)
        }
        translateDescription={(id, description) =>
          ({
            'potdeksel-190': t('options.keralitPotdeksel190'),
            'potdeksel-177': t('options.keralitPotdeksel190'),
            'sponningdeel-143': t('options.keralitSponningdeel143'),
          }[id] || description)
        }
      />

      {/* Dimensions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.dimensions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="width">{t('options.width')}</Label>
            <Input
              id="width"
              {...dimensionInputProps('width', widthInput, setWidthInput)}
            />
          </div>
          <div>
            <Label htmlFor="height">{t('options.height')}</Label>
            <Input
              id="height"
              {...dimensionInputProps('height', heightInput, setHeightInput)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Colors */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.colors')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('options.interiorColor')}</Label>
            <CardDescription className="mb-2">Search by RAL code or color name</CardDescription>
            <ColorPicker
              selectedColor={config.interiorColor || 'white'}
              onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
            />
          </div>
          <div>
            <Label>{t('options.exteriorColor')}</Label>
            <CardDescription className="mb-2">Search by RAL code or color name</CardDescription>
            <ColorPicker
              selectedColor={config.exteriorColor || 'anthracite'}
              onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Panel Lines */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.lineType')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label>{t('options.lineType')}</Label>
            <Select
              value={config.lineType || 'horizontal'}
              onValueChange={(value) => onConfigChange('lineType', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select line type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="horizontal">Horizontal Lines</SelectItem>
                <SelectItem value="vertical">Vertical Lines</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {onAdditionalItemsChange ? (
        <ConfiguratorAdditionalItems
          items={additionalItems}
          onItemsChange={onAdditionalItemsChange}
        />
      ) : null}
    </div>
  );
};

export default KeralitOptionsPanel;
